import { useCallback, useState } from "react";
import { ErrorMessage, Flex, Select, Text } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { QuestionType } from "~/graphql/supergraphEnums";
import { useSetDdqQuestionTypeMutation } from "~/graphql/supergraphHooks";
import { QUESTION_TYPE_LABELS } from "~/pages/ddq/intake/DDQueue/labels";
import { SPACING } from "~/styles/variables";
import * as styles from "./styles_vanilla.css";
import type { TreeQuestion } from "./buildTree";

export type QuestionNodeProps = {
  question: TreeQuestion;
  arenaId: number;
  ddqId: string;
};

const QUESTION_TYPE_OPTIONS = Object.values(QuestionType);

// Renders the question's definition -- number, text, and a type control. No other editing UI
// (title/notes/delete/reorder) exists here yet; this adds the type control only, same
// minimal-surface reasoning as the rest of this read-only tree.
export const QuestionNode = ({ question, arenaId, ddqId }: QuestionNodeProps) => {
  // Optimistic display value: mirrors `question.questionType` until a change is in flight or
  // has failed, at which point it diverges so the Select can show the failed attempt and then
  // revert without waiting on a refetch.
  const [pendingType, setPendingType] = useState<QuestionType | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasChildren = question.children.length > 0;

  const [setDdqQuestionType, { loading }] = useSetDdqQuestionTypeMutation({
    context: withClientContext("federation"),
    // Matches the cache-and-network/refetch convention already used in this tree; avoids
    // threading a refetch callback through the recursive SectionNode/QuestionNode.
    refetchQueries: ["GetDdqStructure"],
  });

  const currentType = pendingType ?? question.questionType;

  const handleChange = useCallback(
    (value: string | number | string[] | number[]) => {
      const nextType = value as QuestionType;
      setErrorMessage(null);

      if (hasChildren) {
        setErrorMessage(
          `Can't change type: this question has ${question.children.length} question(s) nested under it. ` +
            "Support for changing types with nested questions is coming in a later pass.",
        );
        return;
      }

      setPendingType(nextType);
      setDdqQuestionType({
        variables: {
          input: { arenaId, ddqId, questionId: question.id, questionType: nextType },
        },
      })
        .then(() => setPendingType(null))
        .catch((error: unknown) => {
          setErrorMessage(error instanceof Error ? error.message : "Something went wrong.");
          setPendingType(null);
        });
    },
    [arenaId, ddqId, hasChildren, question.children.length, question.id, setDdqQuestionType],
  );

  return (
    <div className={styles.question} data-testid={`ddq-question-${question.id}`}>
      <Flex justifyContent="space-between" alignItems="flex-start" gap={SPACING.SMALL}>
        <Text variant="regular-md">
          {question.number}. {question.questionText}
        </Text>
        <Select
          value={currentType}
          onChange={handleChange}
          disabled={loading}
          hideLabel
          label={`Question type for ${question.number}`}
          data-testid={`ddq-question-type-select-${question.id}`}
        >
          <Select.Section>
            {QUESTION_TYPE_OPTIONS.map((type) => (
              <Select.Option key={type} value={type}>
                {QUESTION_TYPE_LABELS[type]}
              </Select.Option>
            ))}
          </Select.Section>
        </Select>
      </Flex>
      {errorMessage && (
        <ErrorMessage data-testid={`ddq-question-type-error-${question.id}`}>
          {errorMessage}
        </ErrorMessage>
      )}
      {question.children.length > 0 && (
        <Flex flexDirection="column" className={styles.questionChildren}>
          {question.children.map((child) => (
            <QuestionNode key={child.id} question={child} arenaId={arenaId} ddqId={ddqId} />
          ))}
        </Flex>
      )}
    </div>
  );
};
